/**
 * useProductSliderData - Composition hook for ProductSlider products
 *
 * Determines which query to use (category vs SKUs) based on slider config
 * and returns the appropriate products and loading state.
 *
 * This keeps ProductSlider component focused on presentation.
 */
import { useMemo } from 'react'

import { useProductsByCategory } from './useProductsByCategory'
import { useProductsBySkus } from './useProductsBySkus'

import type { HomepageProductSlider, Product } from '../../types'

export interface UseProductSliderDataOptions {
  /** Slider configuration from homepage config */
  slider: HomepageProductSlider
}

export interface UseProductSliderDataResult {
  /** Transformed products for ProductCard */
  products: Product[]
  /** Loading state from the active query */
  isLoading: boolean
  /** Error state from the active query */
  isError: boolean
  /** Error object if any */
  error: Error | null
}

/**
 * Fetch products for a ProductSlider based on its configuration
 *
 * @example
 * const { products, isLoading } = useProductSliderData({ slider })
 */
export const useProductSliderData = ({
  slider,
}: UseProductSliderDataOptions): UseProductSliderDataResult => {
  // Parse SKUs from source_value (comma-separated)
  const skuList = useMemo(() => {
    if (slider.source_type !== 'skus' || !slider.source_value) return []
    return slider.source_value.split(',').map(sku => sku.trim()).filter(Boolean)
  }, [slider.source_type, slider.source_value])

  // Fetch products by category (only when source_type is "category")
  const categoryQuery = useProductsByCategory({
    categoryId: slider.source_value ?? '',
    pageSize: slider.products_count,
    enabled: slider.source_type === 'category' && !!slider.source_value,
  })

  // Fetch products by SKUs (only when source_type is "skus")
  const skusQuery = useProductsBySkus({
    skus: skuList,
    pageSize: slider.products_count,
    enabled: slider.source_type === 'skus' && skuList.length > 0,
  })

  // Return the appropriate query result based on source_type
  return slider.source_type === 'category' ? categoryQuery : skusQuery
}
